import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Min, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import { Range } from "@pdfking/tfunc"

import { PALETTE } from "#constants"
import type { AgendaDay } from "#internal"

import { DayColor } from "../calendar/DayColor"
import { MonthName } from "../calendar/MonthName"
import { PlotNotebookBackground } from "../layout/PlotNotebookBackground"
import { Slice } from "../layout/Slice"
import { PlainText } from "../text/PlainText"
import { NotebookBackground } from "../types/NotebookBackground"
import { DrawAgendaBlock } from "./DrawAgendaBlock"
import { WeekdayName } from "./WeekdayName"

// The hours a working day is ruled by, 8 to 20
const FIRST_HOUR = 8
const HOURS = 13
const HOURS_SHARE = 0.62

/** Weekday `weekday`'s block filling `box`, `pitch` its lines apart, in a
 * holiday's color on a Sunday: an agenda's block (see DrawAgendaBlock), its
 * number the `date`'s; or when by the `hours` a header a line and a half
 * tall — its number, on a square of the primary when `badge`, its weekday's
 * name beside it and its month, unless not `month`, on the far side, the
 * number and the month blank when it has no `date` — and under it a line an
 * hour from 8 to 20 and dotted lines of notes under them, or as its `ruling`
 * has them: its hours that many, `hour` apart from under the header, a dotted
 * line on each half when `halves`, nothing under them. Its names in
 * `language`. */
export const DrawAgendaDay = ({
  badge,
  box,
  date,
  hours,
  label: named,
  language,
  month = true,
  pitch,
  ruling,
  weekday,
}: {
  badge: boolean
  box: SpatialRectangle<Distance>
  date: AgendaDay | undefined
  hours: boolean
  label?: string
  language: string
  month?: boolean
  pitch: Distance
  ruling: { halves: boolean; hour: Distance; hours: number } | undefined
  weekday: number
}): PageShape[] => {
  const label = named ?? WeekdayName(weekday, language).toUpperCase()
  const top = Total(box.y, box.height)
  const header = Scale(pitch, 1.5)
  const number_font = Scale(header, 0.7)
  const font = Scale(pitch, 0.4)
  const color = DayColor(weekday)
  const square = { height: header, width: header, x: box.x, y: Subtract(top, header) }
  const text_x = Total(box.x, header, Scale(font, 0.8))
  const text_width = Subtract(Total(box.x, box.width), text_x)
  // The header's text on its middle
  const middle = Subtract(top, Scale(Total(header, font), 0.5))
  const number = NullQ(date)
    ? []
    : [
        PlainText(
          `${date.day}`,
          square.x,
          square.width,
          Total(square.y, Scale(Subtract(header, number_font), 0.5)),
          number_font,
          badge ? PALETTE.paper : color,
          HorizontalAlign.CENTER
        ),
      ]
  const [, rest] = Slice.top(box, header)
  // By no hours, as the perpetual agenda has it, its number large over its
  // name when dated
  if (!hours)
    return DrawAgendaBlock({
      badge,
      box,
      color,
      label,
      number: NullQ(date) ? undefined : `${date.day}`,
      pitch,
    })
  // An hour's line, the hours filling their share of the day, or the ruling's
  const hour = ruling?.hour ?? Scale(rest.height, HOURS_SHARE / HOURS)
  const count = ruling?.hours ?? HOURS
  const top_of = (lines: number, offset: Distance) => ({
    ...rest,
    height: Scale(hour, lines + 0.5),
    y: Subtract(Total(rest.y, rest.height, offset), Scale(hour, lines + 0.5)),
  })
  return [
    ...(badge
      ? [
          {
            enum: PlotFunction.PLOT_RECT,
            fill_color: PALETTE.primary,
            position: square,
          } as PageShape,
        ]
      : []),
    ...number,
    ...(NullQ(date) || !month
      ? []
      : [
          PlainText(
            MonthName(date.month, language).toUpperCase(),
            text_x,
            text_width,
            middle,
            font,
            PALETTE.primary,
            HorizontalAlign.RIGHT
          ),
        ]),
    PlainText(label, text_x, text_width, middle, font, color),
    ...(hours
      ? [
          // The working day's hours ruled over HOURS_SHARE of it, the rest
          // under them notes, dotted, an hour apart
          ...PlotNotebookBackground({
            background: NotebookBackground.LINES,
            box: top_of(count, ZERO_DISTANCE),
            pitch: hour,
          }),
          ...(ruling?.halves
            ? PlotNotebookBackground({
                background: NotebookBackground.DOTTED_LINES,
                box: top_of(count, Scale(hour, 0.5)),
                pitch: hour,
              })
            : []),
          ...(ruling
            ? []
            : PlotNotebookBackground({
                background: NotebookBackground.DOTTED_LINES,
                box: Slice.top(rest, Scale(hour, HOURS + 1))[1],
                pitch: hour,
              })),
        ]
      : []),
    ...(hours
      ? [...Range(1, count)].map(i =>
          PlainText(
            `${FIRST_HOUR + i - 1}`.padStart(2, "0"),
            box.x,
            Min(box.width, header),
            Total(Subtract(top, Total(header, Scale(hour, i))), Scale(font, 0.4)),
            font,
            PALETTE.lines
          )
        )
      : []),
  ]
}
